"use client";
import React, { useRef, useState } from "react";
import AdditionalResourcesCarousel from "./additional-resources-carousel";
import { ArrowUpRight, RelatedArticleIcon } from "~/components/icons";
import Link from "next/link";
import Image from "next/image";
import AspectRatio from "components/aspect-ratio";
import { BlogDownloadable } from "./blog-downloadable-ai-showcase";

const AdditionalResources = ({ showCaseItem }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<any | null>(null);
  const videos = showCaseItem?.videos;
  const relatedArticles = showCaseItem?.related_articles;

  const goNext = () => {
    swiperRef.current?.swiper?.slideNext();
  };
  const goPrev = () => {
    swiperRef.current?.swiper?.slidePrev();
  };
  const handleSlideChange = (activeIndex: number) => {
    setActiveIndex(activeIndex);
  };
  return (
    <>
      <section className="relative bg-black text-white">
        <div className="absolute top-0 z-[25] h-[1px] w-full bg-white md:block" />
        <div className="container relative py-[120px]">
          <div className="absolute right-[5.5px] top-[-5px] z-[90] h-3 w-3 rotate-45 bg-white md:right-[-5.5px] md:block" />
          <div className="absolute right-[10px] top-[-400px] z-[25] h-[calc(100%+300px)] w-[1px] translate-y-12 bg-white md:right-0 md:block" />
          <div className="bg-black pb-5">
            {showCaseItem?.downloadable && (
              <BlogDownloadable
                downloadable={showCaseItem?.downloadable}
                ctaBtn={showCaseItem?.ctaBtn}
              />
            )}
          </div>
          {showCaseItem.key == "motific" && (
            <div className="relative mb-12 rounded-2xl bg-white px-3 py-12 text-black md:px-12 md:py-16">
              <div className="w-[180px] -translate-x-3 md:w-[280px] md:-translate-x-12">
                <AspectRatio aspectRatio={{ width: 274, height: 59 }}>
                  <Image
                    unoptimized
                    src="/images/pages/blog/mistral-banner.png"
                    fill
                    alt="mistral"
                  />
                </AspectRatio>
              </div>
              <div className="grid grid-cols-1 pt-3 md:grid-cols-2 md:gap-6">
                <div className=" col-span-1 py-5">
                  <h4 className="font-h4 pb-5">
                    Cisco and Mistral AI join forces to revolutionize enterprise
                    AI
                  </h4>
                  <p className="font-p">
                    We’ve partnered with Mistral AI to offer organizations
                    powerful and trustworthy GenAI solutions, beginning with our
                    new SaaS product, Motific. By leveraging Mistral AI's latest
                    Large Language Models (LLMs) across Cisco’s products, we’re
                    reinforcing our commitment to providing superior AI
                    solutions, regardless of the underlying technology.
                  </p>
                </div>
                <div className=" col-span-1">
                  <div className="border-b border-black py-5">
                    <p className="font-btn font-btn-lg pb-2 uppercase">
                      Open and customizable solutions
                    </p>
                    <p className="font-p">
                      With Mistral AI's open, flexible LLMs and the power of
                      Cisco's portfolio and data, we're crafting precise,
                      efficient features to address the industry's rapidly
                      evolving business needs.
                    </p>
                  </div>
                  <div className="py-5">
                    <p className="font-btn font-btn-lg pb-2 uppercase">
                      Sustainability at the forefront 
                    </p>
                    <p className="font-p">
                      Our collaboration is built on a foundation of viability
                      and resilience. By integrating Mistral AI's optimized
                      models, we're aligning with Cisco’s vision for a
                      sustainable future.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(videos?.length > 0 || relatedArticles?.length > 0) && (
            <h3 className="font-h3 pt-8">Additional resources</h3>
          )}
          {videos?.length > 0 && (
            <div className="h-full md:w-full">
              <AdditionalResourcesCarousel
                items={videos}
                swiperRef={swiperRef}
                onSlideChange={handleSlideChange}
                goNext={goNext}
                goPrev={goPrev}
                activeIndex={activeIndex}
              />
            </div>
          )}
        </div>
        {relatedArticles?.length > 0 && (
          <div className="container bg-black pt-[50px] text-white lg:flex lg:gap-16">
            <div className="md:flex-[.6]">
              <div className="flex items-center gap-5">
                <RelatedArticleIcon />
                <h3 className="font-h6 uppercase text-[#159ECD]">
                  THOUGHT LEADERSHIP
                </h3>
              </div>

              <div className="pb-[70px]">
                <h3 className="font-h3 mt-10">
                  {showCaseItem?.relatedArticlesTitle}
                </h3>
                <p className="font-p mt-5">
                  {showCaseItem?.relatedArticlesDescription}
                </p>
              </div>
            </div>
            <div className="md:flex-1 lg:pb-[100px]">
              {relatedArticles.map((articles, i) => (
                <div key={i}>
                  <Link
                    href={articles?.cta?.url}
                    target="_blank"
                    className="group"
                  >
                    <div className="mb-3 inline-block rounded-md bg-white px-2 py-1 font-medium text-black">
                      {articles?.source}
                    </div>
                    <div className="grid grid-cols-4 justify-between gap-2 md:gap-12">
                      <h3 className="font-h4 col-span-3">
                        {articles?.title}
                      </h3>
                      <div className="col-span-1 flex flex-col-reverse items-end justify-end gap-3 uppercase md:flex-row md:items-start md:justify-end">
                        <div className="flex flex-col-reverse items-end gap-3 md:flex-row md:items-center">
                          <h6 className="max-w-[80px] text-right text-sm font-thin uppercase group-hover:underline md:block md:tracking-[.05em]">
                            {articles?.cta?.cta}
                          </h6>
                          <div>
                            <div className="block rounded-md border border-white text-white">
                              <ArrowUpRight size={46} color="white" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {i < relatedArticles.length - 1 && (
                      <hr className="mt-5 border-t border-[#ADADAD] pb-10" />
                    )}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="hidden h-[50px] lg:block"></div>
      </section>
    </>
  );
};

export default AdditionalResources;
